import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { ShopType } from '@reims-local/types';
import { authApi } from '../lib/api';

interface InvitationView {
  email: string;
  expires_at: string;
  shop: {
    id: string;
    name: string;
    slug: string;
    shop_type: ShopType;
    address: string | null;
    phone: string | null;
    partner_included: boolean;
  };
}

export function InvitationActivate({
  onActivated,
}: {
  onActivated: (user: { id: string; email: string; role: string; shop_id: string | null }) => void;
}) {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [view, setView] = useState<InvitationView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [cguAccepted, setCguAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) return;
    authApi
      .getInvitation(token)
      .then((res) => {
        setView({ email: res.invitation.email, expires_at: res.invitation.expires_at, shop: res.shop });
        setError(null);
      })
      .catch((err) => {
        const code = (err as Error).message;
        setError(
          code === 'expired'
            ? 'Ce lien a expiré. Contactez-nous pour recevoir une nouvelle invitation.'
            : code === 'already_activated'
              ? 'Ce compte a déjà été activé. Connectez-vous.'
              : 'Lien invalide.',
        );
      })
      .finally(() => setLoading(false));
  }, [token]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    if (!cguAccepted) {
      setError('Vous devez accepter les CGU.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await authApi.activateInvitation(token, {
        password,
        name,
        phone: phone || undefined,
        cgu_version: '1.0',
      });
      const me = await authApi.me();
      onActivated(me.user);
      navigate('/');
    } catch (err) {
      const code = (err as Error).message;
      setError(
        code === 'expired'
          ? 'Ce lien a expiré.'
          : code === 'email_already_used'
            ? 'Cet email est déjà utilisé par un autre compte.'
            : code === 'invalid_input'
              ? 'Données invalides. Mot de passe : min 8 caractères, 1 majuscule, 1 chiffre.'
              : 'Activation impossible.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="onboarding-wrap">
        <div className="card onboarding-card">Chargement de votre invitation…</div>
      </div>
    );
  }

  if (!view || error) {
    return (
      <div className="onboarding-wrap">
        <div className="card onboarding-card">
          <h2 style={{ marginTop: 0, color: 'var(--primary)' }}>Invitation</h2>
          <div className="alert alert-error">{error ?? 'Lien invalide.'}</div>
          <a href="/login">Aller à la connexion</a>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-wrap">
      <form className="card onboarding-card" onSubmit={submit}>
        <h2 style={{ marginTop: 0, color: 'var(--primary)' }}>Bienvenue sur Reims Local</h2>
        <p className="muted" style={{ marginTop: -8 }}>
          Activation du compte pour <strong>{view.shop.name}</strong>
        </p>

        {error && <div className="alert alert-error">{error}</div>}

        <div className="card" style={{ background: '#f9f9f7', marginBottom: 20 }}>
          <div className="muted" style={{ marginBottom: 8 }}>Votre boutique</div>
          <div><strong>{view.shop.name}</strong></div>
          <div className="muted">{view.shop.address ?? 'Adresse non renseignée'}</div>
          <div className="muted">Type : {view.shop.shop_type}</div>
          {view.shop.partner_included && <div className="badge blue" style={{ marginTop: 8 }}>Vitrines de Reims — inclus</div>}
        </div>

        <div className="form-row">
          <label>Email (lié à votre invitation)</label>
          <input type="email" value={view.email} disabled />
        </div>

        <div className="form-row">
          <label>Votre nom</label>
          <input required value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        <div className="form-row">
          <label>Téléphone (optionnel)</label>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>

        <div className="form-row">
          <label>Mot de passe (min 8 caractères, 1 majuscule, 1 chiffre)</label>
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>

        <div className="form-row">
          <label>Confirmer le mot de passe</label>
          <input type="password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
        </div>

        <div className="form-row-inline">
          <input
            id="cgu"
            type="checkbox"
            checked={cguAccepted}
            onChange={(e) => setCguAccepted(e.target.checked)}
            style={{ width: 'auto' }}
          />
          <label htmlFor="cgu">J’accepte les conditions générales d’utilisation.</label>
        </div>

        <button className="primary" type="submit" style={{ width: '100%' }} disabled={submitting}>
          {submitting ? 'Activation…' : 'Activer ma boutique'}
        </button>
      </form>
    </div>
  );
}
